import { css } from "@emotion/react";
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { RowInfo } from "react-table";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { ReportType } from "constants/reports/report-type";
import { getChartFormatter } from "helpers/chart";
import { duration } from "helpers/date";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { IStoreState } from "interfaces/storeState";
import { IHandlingTimeByStatusPerEntity } from "services/api/report/interfaces";
import { selectAgents } from "store/entities/agents";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { selectTeams } from "store/entities/teams";
import { getIsReportFetching, getReportData } from "store/views/reports";
import { withVar } from "themes/helpers";

import { ChartPanel, Filters, ReportLocalTimeLabel } from "../components";
import { ChartBreakdown } from "../components/ChartBreakdown";
import { AgentCell, TeamCell, ValueCell } from "../components/PerformanceTableCells";
import { PerformanceTablePromo } from "../components/PerformanceTablePromo";
import { getChartOptions } from "../helpers";
import { containerFluid } from "../styled/layout";

import { formatDurationWithFallback } from "./helpers";

function pointFormatter(point: { series: { name: string }; y: number }): string {
  const value = duration(point.y);
  return `${point.series.name}: <strong>${value}</strong><br />`;
}

const chartOptions = {
  yAxis: {
    labels: {
      format: "{value} h",
      formatter(this: { value: number }): string {
        return duration(this.value);
      },
    },
  },
  tooltip: {
    formatter: getChartFormatter(pointFormatter),
  },
  forceOptions: {
    fixedSeriesHeader: "Status",
  },
};

export const HandlingTimeByStatus: FC = () => {
  useLastVisitedReport(ReportType.HandlingTimeByStatus);
  const isFetching = useSelector((state: IStoreState) => getIsReportFetching(state, ReportType.HandlingTimeByStatus));
  const data = useSelector((state: IStoreState) => getReportData(state, ReportType.HandlingTimeByStatus));
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const isTeam = useSelector(selectCurrentSubscriptionPlan) === Plan.Team;
  const perTeamData = data?.handlingTimeByPerTeam;
  const perTeamDataTotal = data?.handlingTimeByPerTeam.find(item => item.id === "total");
  const {
    open: teamOpenTotal,
    pending: teamPendingTotal,
    onhold: teamOnholdTotal,
    total: teamTotal,
  } = perTeamDataTotal ?? {};
  const perAgentData = data?.handlingTimeByPerAgent;
  const perAgentDataTotal = data?.handlingTimeByPerAgent.find(item => item.id === "total");
  const {
    open: agentOpenTotal,
    pending: agentPendingTotal,
    onhold: agentOnholdTotal,
    total: agentTotal,
  } = perAgentDataTotal ?? {};
  const chartLegend = useMemo(() => data?.handlingTimeByStatus.summary, [data]);
  const teamColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => (
          <TeamCell value={value} row={row} teamName={teams[value]?.name} totalLabel="Median" />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableOpenedHeaderStyles}></span>
            <span>Open</span>
          </>
        ),
        accessor: "open",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={teamOpenTotal} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tablePendingHeaderStyles}></span>
            <span>Pending</span>
          </>
        ),
        accessor: "pending",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={teamPendingTotal} original={original} />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableOnHoldHeaderStyles}></span>
            <span>On Hold</span>
          </>
        ),
        accessor: "onhold",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={teamOnholdTotal} original={original} />
        ),
        className: "odd",
      },
      {
        Header: <strong>Resolution time</strong>,
        accessor: "total",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={teamTotal} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  const agentColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => {
          const agent = agents[value];
          const { avatar, name } = agent ?? {};
          return <AgentCell value={value} row={row} agentName={name} avatar={avatar} totalLabel="Median" />;
        },
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableOpenedHeaderStyles}></span>
            <span>Open</span>
          </>
        ),
        accessor: "open",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={agentOpenTotal} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tablePendingHeaderStyles}></span>
            <span>Pending</span>
          </>
        ),
        accessor: "pending",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={agentPendingTotal} original={original} />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableOnHoldHeaderStyles}></span>
            <span>On Hold</span>
          </>
        ),
        accessor: "onhold",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={agentOnholdTotal} original={original} />
        ),
        className: "odd",
      },
      {
        Header: <strong>Resolution time</strong>,
        accessor: "total",
        Cell: ({ value, original }: { value: number; original: IHandlingTimeByStatusPerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={agentTotal} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  const chartOptionsData = useMemo(
    () =>
      data &&
      getChartOptions(
        data.handlingTimeByStatus.series,
        data.handlingTimeByStatus.labels,
        undefined,
        null,
        chartOptions
      ),
    [data]
  );

  return (
    <div>
      <Filters disableDistribution />
      <div css={containerFluid}>
        <ChartPanel
          title="Handling time by status"
          description="Data shown below is based on either solved and/or closed tickets from the selected period"
          tooltip="The median time a ticket remains in Open, Pending, and On hold statuses."
          loading={isFetching}
          legend={chartLegend}
          options={chartOptionsData}
          breakdownValueParser={formatDurationWithFallback}
          breakdownCustomTitle="Daily handling time breakdown"
          customComponent={
            <>
              <ChartBreakdown
                customTitle="Handling time per team"
                breakdownData={{ columns: teamColumns, data: perTeamData }}
                loading={isFetching}
                truncateData={perTeamData && perTeamData.length > 5}
              />
              <Block marginBottom={withVar(SpacingToken.Spacing7)} />
              <ChartBreakdown
                fallbackComponent={isTeam && <PerformanceTablePromo />}
                customTitle="Handling time per agent"
                breakdownData={{ columns: agentColumns, data: perAgentData }}
                loading={isFetching}
                truncateData={perAgentData && perAgentData.length > 5}
                disableExpand={isTeam}
              />
              <Block marginBottom={withVar(SpacingToken.Spacing7)} />
            </>
          }
        />
        <ReportLocalTimeLabel />
      </div>
    </div>
  );
};

const tableHeaderStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: ${withVar(SpacingToken.Spacing1)};
`;

const tableOpenedHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.SurfaceAccentEmphasisHighInfo)};
`;
const tablePendingHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorGray400)};
`;

const tableOnHoldHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.SurfaceAccentEmphasisHighInfo)};
`;
